import { formatNumber, formatPercent } from '@angular/common';
import {
    MetricPerformanceData,
    MetricTooltipData,
    TrendData,
} from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { bgGradient } from '../../common/pipe/bg-gradient';
import { epicDependencies } from '../../store';
import { CoachingItemsOaScoreTypeTranslationMap } from '../constants/coaching-items-score-type-translation-map';
import { MyCoachingCardGroup } from '../models/my-coaching-card-group.model';
import { StagingKpiThreshold } from '../models/staging-kpi-threshold.model';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { getThresholdData } from './get-threshold-data.function';

export const prepareMetricPerformanceData = (
    oaSummaryItem: MyCoachingCardGroup,
    coachingCardThreshold: StagingKpiThreshold[],
    isTypeFloa?: boolean,
    superGroupScore?: number,
): MetricPerformanceData | null => {
    if (epicDependencies == null || epicDependencies.injector == null) {
        return null;
    }

    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    let dataRowNameLabel: string;

    if (isTypeFloa === true) {
        dataRowNameLabel = translateService.instant(
            TranslationKeyMap._4005_FLOA__,
        );
    } else {
        dataRowNameLabel = translateService.instant(
            CoachingItemsOaScoreTypeTranslationMap[oaSummaryItem.type],
        );
    }
    const metricNameTooltip = `${String(
        translateService.instant(TranslationKeyMap._11177_LAUNCH),
    )} ${String(dataRowNameLabel)}`;

    if (oaSummaryItem.score == null) {
        const noTargetDataMessage = translateService.instant(
            TranslationKeyMap._11172_NO_RESULTS_TO_DISPLAY,
        );
        return {
            metricName: dataRowNameLabel,
            metricNameTooltip,
            trendValues: null,
            progressBarData: null,
            color: null,
            noActualDataMessage: null,
            noTargetDataMessage,
            isVisible: false,
            isExpandable: false,
        };
    }

    let noTargetDataMessage = null;

    if (oaSummaryItem.target == null || oaSummaryItem.target === 0) {
        noTargetDataMessage = translateService.instant(
            TranslationKeyMap._11173_NO_TARGET_THRESHOLDS_CONFIGURED,
        );
    }

    const calculatedThresholds = getThresholdData(
        oaSummaryItem,
        coachingCardThreshold,
    );
    const trendValues = calculateTrendValues(
        oaSummaryItem.score * 100,
        oaSummaryItem.prevScore != null ? oaSummaryItem.prevScore * 100 : 0,
        oaSummaryItem.color,
        translateService,
    );

    const actualLabel: string = translateService.instant(
        TranslationKeyMap._725_ACTUAL,
    );
    const targetLabel: string = translateService.instant(
        TranslationKeyMap._726_TARGET,
    );

    const superGroupLabel: string = translateService.instant(
        TranslationKeyMap._67_SUPER_GROUP,
    );

    const actualValueTooltip = prepareMetricTooltipData(
        actualLabel,
        Math.round(oaSummaryItem.score * 100),
        `${actualLabel}: ${formatNumber(
            oaSummaryItem.score * 100,
            'en-CA',
            '1.0-0',
        )}`,
        translateService,
    );
    const targetValueTooltip = prepareMetricTooltipData(
        targetLabel,
        oaSummaryItem.target * 100,
        `${targetLabel}: ${formatActualValue(oaSummaryItem.target * 100)}`,
        translateService,
    );

    const superGroupTooltip = prepareMetricTooltipData(
        superGroupLabel,
        superGroupScore ?? 0,
        `${superGroupLabel}: ${formatActualValue(superGroupScore)}`,
        translateService,
    );

    return {
        metricName: dataRowNameLabel,
        metricNameTooltip,
        trendValues,
        progressBarData: {
            thresholds: calculatedThresholds,
            actualValue: actualValueTooltip,
            targetValue: targetValueTooltip,
            superGroupValue: superGroupTooltip,
            minValue: 0,
            maxValue: 100,
        },
        color: trendValues?.thresholdColor,
        noActualDataMessage: null,
        noTargetDataMessage,
        isVisible: true,
        isExpandable: false,
    };
};

const prepareMetricTooltipData = (
    title: string,
    value: number | undefined,
    tooltipText: string,
    translateService: TranslateService,
): MetricTooltipData | undefined => {
    if (value !== undefined) {
        return {
            title,
            value,
            tooltipText: translateService.instant(tooltipText),
        };
    }
    return undefined;
};
const formatActualValue = (value: number): string => {
    if (value < 10) {
        return formatNumber(value, 'en-CA', '1.2-2');
    } else if (value < 100) {
        return formatNumber(value, 'en-CA', '1.1-1');
    }
    return formatNumber(value, 'en-CA', '1.0-0');
};

export const calculateTrendValues = (
    currentActual: number,
    previousActual: number,
    thresholdColor: string,
    translateService: TranslateService,
): TrendData => {
    let trendArrow = '';
    let trendValue = '--';
    const trendViewText = translateService.instant(
        TranslationKeyMap._10075_VIEW_TREND,
    );
    let trendColor = '';

    if (previousActual == null || currentActual == null) {
        trendValue = '--';
        trendArrow = '';
    } else {
        const trendDifference =
            (currentActual - previousActual) / previousActual;

        if (currentActual === previousActual) {
            trendValue = formatPercent(0, 'en-CA', '1.2-2');
            trendArrow = 'arrow-left-right';
            trendColor = '#000';
        } else {
            if (Math.abs(trendDifference) < 0.1) {
                trendValue = formatPercent(
                    Math.abs(trendDifference),
                    'en-CA',
                    '1.2-2',
                );
            } else if (Math.abs(trendDifference) < 1) {
                trendValue = formatPercent(
                    Math.abs(trendDifference),
                    'en-CA',
                    '1.1-1',
                );
            } else {
                trendValue = formatPercent(
                    Math.abs(trendDifference),
                    'en-CA',
                    '1.0-0',
                );
            }

            if (currentActual > previousActual) {
                trendArrow = 'arrow-up';
                trendColor = '#137e0f';
            } else {
                trendArrow = 'arrow-down';
                trendColor = '#ce2521';
            }
        }
    }

    return {
        actual: formatNumber(currentActual, 'en-CA', '1.0-0'),
        trendColor,
        thresholdColor,
        thresholdColorGradient: bgGradient({
            color: thresholdColor,
        }),
        trendValue,
        trendArrow,
        trendViewText,
    };
};
